<template>
  <div id="tag-filter">
    <DxRadioGroup v-model:value="tagFilterMode"
                  :items="['All', 'Any', 'None']"
                  layout="horizontal"
                  @value-changed="modifyTagSearchObject"
    />

    <DxTextBox placeholder="Search Tags..."
               v-model:value="tagSearchText"
               :show-clear-button="true"
               @input="searchTags"
    />

    <DxDataGrid :data-source="filteredTagItems"
                :load-panel="{ enabled: false }"
                :remote-operations="false"
                :row-alternation-enabled="true"
                :show-column-headers="false"
                :show-column-lines="false"
                :show-row-lines="false"
                :height="400"
    >
      <DxScrolling mode="virtual"/>
      <DxPaging :page-size="25"/>
      <DxColumn cell-template="cell-template"/>

      <template #cell-template="{ data: tag }">
        <TagRowTemplate :tag-id="tag.data.tag_id"
                        :is-checked="activeTagIds.includes(tag.data.tag_id)"
                        :tag-name="tag.data.name"
                        :tag-color="tag.data.color"
                        :tag-count="tag.data.count"
                        :tag-show-count="true"
                        @checkbox-toggle="modifyTagSearchObject"
        />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";
  import { storeToRefs } from "pinia";
  import { DxDataGrid, DxScrolling, DxPaging, DxColumn } from "devextreme-vue/data-grid";
  import { DxRadioGroup } from "devextreme-vue";
  import { DxTextBox } from "devextreme-vue/text-box";
  import TagRowTemplate from "@/components/search-page/search-tool-drawer/TagRowTemplate.vue";
  import { useSearchPageStore } from "@/stores/searchPageStore.js";

  const searchPageStore = useSearchPageStore()
  const { tagFilterItems, searchBarObjects } = storeToRefs(searchPageStore)

  const tagFilterMode = ref('All')

  // filter the tag list by the search term
  const tagSearchText = ref('')
  // we use the @input event to trigger immediate updates to the search results
  const searchTags = (search) => tagSearchText.value = search.event.currentTarget.value
  const filteredTagItems = computed(() => {
    if (!tagSearchText.value)
      return tagFilterItems.value
    return tagFilterItems.value.filter(tag =>
      tag.name.toLowerCase().includes(tagSearchText.value.toLowerCase())
    )
  })

  // used to maintain our one source of truth and pulling the "checked" value of tags down into the TagRowTemplate
  const activeTagIds = computed(() => {
    const tagObjectIndex = searchBarObjects.value.findIndex(obj => obj.type === 'tag')
    if (tagObjectIndex === -1)
      return []
    return Object.keys(searchBarObjects.value[tagObjectIndex].tags).map(key => Number(key))
  })

  const newTagObjectTemplate = { type: 'tag', text: '', mode: tagFilterMode.value, tags: {} }
  const modifyTagSearchObject = ({ isChecked, tagName, tagId }) => {
    const searchObjectCopy = JSON.parse(JSON.stringify(searchBarObjects.value))
    const tagObjectIndex = searchObjectCopy.findIndex(obj => obj.type === 'tag')

    const tagObject = searchObjectCopy[tagObjectIndex] ?? JSON.parse(JSON.stringify(newTagObjectTemplate))
    if (isChecked)
      tagObject.tags[tagId] = tagName
    else
      delete tagObject.tags[tagId]
    tagObject.text = tagFilterMode.value + ' of: ' + Object.values(tagObject.tags).join(", ")
    tagObject.mode = tagFilterMode.value

    if (tagObjectIndex !== -1)
      searchObjectCopy[tagObjectIndex] = tagObject
    else
      searchObjectCopy.push(tagObject)

    if (Object.entries(tagObject.tags).length === 0)
      searchObjectCopy.splice(tagObjectIndex, 1)

    searchBarObjects.value = searchObjectCopy
  }
</script>

<style scoped lang="scss">
  #tag-filter {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
</style>
